import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { useSubscribe } from "@/contexts/RealtimeContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Vote, Plus, X, Trophy, Users, PenLine } from "lucide-react";

export default function EVotingAdminPage() {
  const { propertyId } = useProperty();
  const [ballots, setBallots] = useState([]);
  const [residents, setResidents] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", options: ["Yes", "No"], closes_at: "" });
  const [proxyOpen, setProxyOpen] = useState(false);
  const [proxyForm, setProxyForm] = useState({ grantor_id: "", proxy_holder_id: "", vote_id: "" });

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const [b, u, p] = await Promise.all([
        api.get(`/votes?property_id=${propertyId}`),
        api.get(`/admin/residents?property_id=${propertyId}`),
        api.get(`/admin/proxies?property_id=${propertyId}`),
      ]);
      setBallots(b.data.items || []);
      setResidents(u.data.items || []);
      setProxies(p.data.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/EVotingAdminPage.jsx: swallowed', err); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  useSubscribe(propertyId ? `property:${propertyId}` : null, ({ event }) => {
    if (event === "vote.new" || event === "vote.closed") load();
  });

  const loadDetails = useCallback(async (vid) => {
    try {
      const { data } = await api.get(`/admin/votes/${vid}/ballots`);
      setDetails(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, []);

  useEffect(() => { if (selected) loadDetails(selected.id); }, [selected, loadDetails, ballots]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const options = form.options.map((o) => o.trim()).filter(Boolean);
    if (options.length < 2) return toast.error("At least 2 options");
    if (!form.closes_at) return toast.error("Set a close date");
    try {
      await api.post(`/admin/votes?property_id=${propertyId}`, {
        title: form.title, description: form.description || null,
        options, closes_at: new Date(form.closes_at).toISOString(),
      });
      toast.success("Ballot opened — residents notified");
      setNewOpen(false);
      setForm({ title: "", description: "", options: ["Yes", "No"], closes_at: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const close = async (v) => {
    if (!window.confirm(`Close ballot "${v.title}"? This cannot be undone.`)) return;
    try {
      await api.patch(`/admin/votes/${v.id}/close`);
      toast.success("Ballot closed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const grantProxy = async () => {
    if (!proxyForm.grantor_id || !proxyForm.proxy_holder_id) return toast.error("Pick both people");
    if (proxyForm.grantor_id === proxyForm.proxy_holder_id) return toast.error("Grantor ≠ holder");
    try {
      await api.post(`/admin/proxies?property_id=${propertyId}`, {
        grantor_id: proxyForm.grantor_id,
        proxy_holder_id: proxyForm.proxy_holder_id,
        vote_id: proxyForm.vote_id || null,
      });
      toast.success("Proxy granted");
      setProxyOpen(false);
      setProxyForm({ grantor_id: "", proxy_holder_id: "", vote_id: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const revokeProxy = async (p) => {
    if (!window.confirm(`Revoke proxy from ${p.grantor_name} to ${p.proxy_holder_name}?`)) return;
    try {
      await api.delete(`/admin/proxies/${p.id}`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const setOption = (idx, value) => setForm((f) => ({ ...f, options: f.options.map((o, i) => i === idx ? value : o) }));
  const addOption = () => setForm((f) => ({ ...f, options: [...f.options, ""] }));
  const removeOption = (idx) => setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));

  const open = ballots.filter((b) => b.status !== "closed");
  const closed = ballots.filter((b) => b.status === "closed");

  return (
    <div>
      <PageHeader
        eyebrow="Governance"
        title="E-Voting & Proxies"
        subtitle="Open ballots, watch live tallies, grant proxies, and archive closed decisions."
        actions={
          <div className="flex items-center gap-2">
            <Dialog open={proxyOpen} onOpenChange={setProxyOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-white/10 rounded-full" data-testid="grant-proxy-btn">
                  <PenLine className="h-3.5 w-3.5 mr-1" /> Grant proxy
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-white/10">
                <DialogHeader><DialogTitle>Grant proxy</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Grantor (giving up their vote)</Label>
                    <Select value={proxyForm.grantor_id} onValueChange={(v) => setProxyForm({ ...proxyForm, grantor_id: v })}>
                      <SelectTrigger data-testid="proxy-grantor" className="bg-white/[0.02] border-white/10"><SelectValue placeholder="Choose resident…" /></SelectTrigger>
                      <SelectContent className="max-h-64">{residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} — Unit {r.unit || "—"}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Proxy holder (voting for grantor)</Label>
                    <Select value={proxyForm.proxy_holder_id} onValueChange={(v) => setProxyForm({ ...proxyForm, proxy_holder_id: v })}>
                      <SelectTrigger data-testid="proxy-holder" className="bg-white/[0.02] border-white/10"><SelectValue placeholder="Choose resident…" /></SelectTrigger>
                      <SelectContent className="max-h-64">{residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} — Unit {r.unit || "—"}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Scope</Label>
                    <Select value={proxyForm.vote_id || "all"} onValueChange={(v) => setProxyForm({ ...proxyForm, vote_id: v === "all" ? "" : v })}>
                      <SelectTrigger data-testid="proxy-scope" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All open ballots</SelectItem>
                        {open.map((v) => <SelectItem key={v.id} value={v.id}>{v.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="proxy-save" className="w-full bg-orange-500 hover:bg-orange-600" onClick={grantProxy}>Grant</Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={newOpen} onOpenChange={setNewOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="new-ballot-btn">
                  <Plus className="h-4 w-4 mr-1" /> New ballot
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-white/10 max-w-lg">
                <DialogHeader><DialogTitle>Open new ballot</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Title</Label><Input data-testid="ballot-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Approve $85k roof replacement" /></div>
                  <div><Label>Description (optional)</Label><Textarea rows={3} data-testid="ballot-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div>
                    <Label>Options</Label>
                    <div className="space-y-1">
                      {form.options.map((o, idx) => (
                        <div key={idx} className="flex gap-1">
                          <Input data-testid={`ballot-option-${idx}`} value={o} onChange={(e) => setOption(idx, e.target.value)} placeholder={`Option ${idx + 1}`} />
                          {form.options.length > 2 && (
                            <Button size="icon" variant="ghost" onClick={() => removeOption(idx)} data-testid={`ballot-option-rm-${idx}`}>
                              <X className="h-4 w-4 text-rose-300" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button size="sm" variant="outline" className="border-white/10 mt-1" onClick={addOption} data-testid="ballot-add-option">
                        <Plus className="h-3 w-3 mr-1" /> Add option
                      </Button>
                    </div>
                  </div>
                  <div><Label>Closes at</Label><Input type="datetime-local" data-testid="ballot-closes" value={form.closes_at} onChange={(e) => setForm({ ...form, closes_at: e.target.value })} /></div>
                  <Button data-testid="ballot-submit" className="w-full bg-orange-500 hover:bg-orange-600" onClick={create}>Open ballot</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Tabs defaultValue="ballots">
        <TabsList className="bg-white/[0.03] border border-white/10">
          <TabsTrigger value="ballots" data-testid="tab-ballots">Ballots</TabsTrigger>
          <TabsTrigger value="proxies" data-testid="tab-proxies">Proxies ({proxies.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="ballots" className="mt-4">
          {open.length > 0 && <div className="overline mb-2">Open</div>}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {open.map((v) => (
              <BallotCard key={v.id} v={v} onSelect={() => setSelected(v)} onClose={() => close(v)} selected={selected?.id === v.id} details={selected?.id === v.id ? details : null} />
            ))}
          </div>

          {closed.length > 0 && <div className="overline mt-6 mb-2">Archive</div>}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {closed.map((v) => (
              <BallotCard key={v.id} v={v} onSelect={() => setSelected(v)} closed selected={selected?.id === v.id} details={selected?.id === v.id ? details : null} />
            ))}
          </div>

          {ballots.length === 0 && <Card className="border-dashed border-white/10 p-10 text-center text-muted-foreground">No ballots yet.</Card>}
        </TabsContent>

        <TabsContent value="proxies" className="mt-4">
          <Card className="border-white/10 bg-card overflow-hidden">
            <div className="divide-y divide-white/5">
              {proxies.map((p) => (
                <div key={p.id} className="px-4 py-3 flex items-center gap-3" data-testid={`proxy-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-medium">{p.grantor_name}</span>
                      <span className="text-zinc-500"> → </span>
                      <span className="font-medium">{p.proxy_holder_name}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {p.vote_id ? "Single ballot" : "All open ballots"} · granted {new Date(p.created_at).toLocaleString()}
                    </div>
                  </div>
                  {p.revoked ? (
                    <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10">Revoked</Badge>
                  ) : (
                    <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300" onClick={() => revokeProxy(p)} data-testid={`proxy-revoke-${p.id}`}>Revoke</Button>
                  )}
                </div>
              ))}
              {proxies.length === 0 && <div className="px-4 py-8 text-center text-muted-foreground">No proxies granted.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BallotCard({ v, onSelect, onClose, selected, details, closed }) {
  const tally = v.tally || {};
  const total = Object.values(tally).reduce((a, b) => a + b, 0) || 1;
  const winner = Object.entries(tally).reduce((best, [k, cnt]) => cnt > (best?.[1] || 0) ? [k, cnt] : best, null);

  return (
    <Card
      className={"border-white/10 bg-card p-5 cursor-pointer transition " + (selected ? "ring-1 ring-orange-500/50" : "hover:bg-white/[0.02]")}
      onClick={onSelect}
      data-testid={`ballot-${v.id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="overline flex items-center gap-1">
            <Vote className="h-3 w-3" /> {closed ? "Closed" : "Open"}
          </div>
          <div className="text-lg font-semibold font-display mt-1">{v.title}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Closes {v.closes_at ? new Date(v.closes_at).toLocaleDateString() : "—"}</div>
        </div>
        {!closed && onClose && (
          <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid={`ballot-close-${v.id}`}>Close</Button>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        {(v.options || []).map((opt) => {
          const cnt = tally[opt] || 0;
          const pct = Math.round((cnt / total) * 100);
          const isWinner = winner && winner[0] === opt && winner[1] > 0;
          return (
            <div key={opt}>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1">
                  {isWinner && closed && <Trophy className="h-3 w-3 text-orange-400" />}
                  <span className={isWinner && closed ? "text-orange-300 font-medium" : ""}>{opt}</span>
                </div>
                <div className="text-zinc-500 tabular-nums">{cnt} · {pct}%</div>
              </div>
              <div className="h-1.5 bg-white/[0.04] rounded-full mt-1 overflow-hidden">
                <div className={"h-full transition-all " + (isWinner && closed ? "bg-orange-400" : "bg-indigo-400")} style={{ width: pct + "%" }} />
              </div>
            </div>
          );
        })}
      </div>

      {selected && details && (
        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2">
            <Users className="h-3 w-3" /> Turnout {details.turnout_pct}% ({details.ballots.length} / {details.eligible_voters})
          </div>
          <div className="max-h-40 overflow-y-auto scroll-slim space-y-1">
            {details.ballots.slice(0, 20).map((b) => (
              <div key={b.id} className="text-[11px] flex justify-between text-zinc-400" data-testid={`ballot-cast-${b.id}`}>
                <span className="truncate">{b.user?.name || "Anonymous"}</span>
                <span className="text-zinc-300">{b.option}</span>
              </div>
            ))}
            {details.ballots.length === 0 && <div className="text-[11px] text-zinc-500">No votes cast yet.</div>}
          </div>
        </div>
      )}
    </Card>
  );
}
